<template>
  <div class="bf-page">
    <PageTitle
      pageTitle="Dashboard"
      :subtitle="`Atualizado por último em ${lastUpdated}`"
    >
      <template #actions>
        <DashboardFilters :last-updated="lastUpdated" />
      </template>
    </PageTitle>

    <IntegrationsStats
      :is-loading="isLoadingIntegrationDashboardInfo"
      :data="integrationDashboard"
    />

    <div class="dashboard-charts">
      <IntegratedLines
        :is-loading="isLoadingIntegrationDashboardInfo"
        :data="integrationDashboard"
      />

      <LineDivisionByIntegration
        :is-loading="isLoadingIntegrationDashboardInfo"
        :data="integrationDashboard"
      />
    </div>

    <RecentExecutions />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, Ref, ref, watch } from "vue";

import { useAuthStore } from "@/stores/auth.store";
import { useDashboardStore } from "@/stores/dashboard.store";

import { useDashboard } from "@/composables/useDashboard";

import DashboardFilters from "@/components/Dashboard/Filters/DashboardFilters.vue";

import PageTitle from "@/components/Common/PageTitle.vue";

import IntegrationsStats from "@/components/Dashboard/IntegrationsStats.vue";
import RecentExecutions from "@/components/Dashboard/RecentExecutions.vue";
import IntegratedLines from "@/components/Charts/IntegratedLines.vue";
import LineDivisionByIntegration from "@/components/Charts/LineDivisionByIntegration.vue";

export default defineComponent({
  name: "DashboardPage",
  components: {
    DashboardFilters,
    PageTitle,
    IntegrationsStats,
    RecentExecutions,
    IntegratedLines,
    LineDivisionByIntegration,
  },
  setup() {
    const authStore = useAuthStore();
    const dashboardStore = useDashboardStore();

    const { integrationDashboard, getIntegrationDashboard } = useDashboard();

    const user: Ref = ref(authStore.getUser);

    const isLoadingIntegrationDashboardInfo = ref(true);
    const lastUpdated = ref(new Date().toLocaleString());

    const getStartAndEndOfCurrentMonth = () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      const formatDateTime = (date: Date) => {
        const datePart = date.toISOString().split("T")[0];
        const timePart = date.toTimeString().split(" ")[0];
        return `${datePart} ${timePart}`;
      };

      return {
        startOfMonth: formatDateTime(startOfMonth),
        endOfMonth: formatDateTime(endOfMonth),
      };
    };

    const fetchIntegrationDashboardData = async (
      initial_datetime?: string,
      final_datetime?: string
    ) => {
      isLoadingIntegrationDashboardInfo.value = true;

      try {
        await getIntegrationDashboard(initial_datetime, final_datetime);
        lastUpdated.value = new Date().toLocaleString();
      } catch (error) {
        console.error("Erro ao buscar dados de dashboard:", error);
      } finally {
        isLoadingIntegrationDashboardInfo.value = false;
      }
    };

    onMounted(() => {
      const { startOfMonth, endOfMonth } = getStartAndEndOfCurrentMonth();

      // Define os filtros iniciais no store (refletem na UI de filtros)
      dashboardStore.filters = {
        initial_datetime: startOfMonth,
        final_datetime: endOfMonth,
      };

      // Dispara o carregamento inicial de forma explícita, sem depender do watcher
      fetchIntegrationDashboardData(startOfMonth, endOfMonth);

      // Passa a observar mudanças de filtro apenas após o carregamento inicial,
      // evitando um fetch duplicado no mount
      watch(
        () => dashboardStore.filters,
        (newFilters) => {
          fetchIntegrationDashboardData(
            newFilters.initial_datetime,
            newFilters.final_datetime
          );
        },
        { deep: true }
      );
    });

    // onUnmounted(() => {
    //   // Remove the event listener when the component is destroyed
    //   EventBus.off("integration-update", () => {
    //     fetchIntegrationDashboardData();
    //   });
    // });

    return {
      user,
      lastUpdated,
      isLoadingIntegrationDashboardInfo,
      integrationDashboard,
    };
  },
});
</script>

<style lang="scss" scoped>
.dashboard-charts {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  margin-top: 16px;
}

@media only screen and (max-width: 991px) {
  .dashboard-charts {
    grid-template-columns: 1fr;
  }
}
</style>
